import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  OFFER_LINK_HEADER,
  type CandidateDocumentsView,
  type CloseOutJobRequest,
  type CloseOutResult,
  type ClosureView,
  type ConfirmJoiningRequest,
  type EmployeeOption,
  type HireView,
  type JoiningDocumentsView,
  type ReviewDocumentRequest,
} from '@nest-recruit/shared';
import { apiGet, apiPost, request, type ApiError } from '../../../lib/api-client';

/**
 * S24 — OFR-04 (the hire), OFR-05 (closure), OFR-06 (exits) and the candidate's documents on
 * their offer page. The server's realtime hints name `hires`, `closure` and `offers`, so a
 * document the candidate uploads appears on the recruiter's screen without a reload.
 */

export const HIRES_KEY = ['hires'] as const;
export const CLOSURE_KEY = ['closure'] as const;

export function hireKey(offerId: string): readonly unknown[] {
  return [...HIRES_KEY, 'offer', offerId];
}

export function applicationDocumentsKey(applicationId: string): readonly unknown[] {
  return [...HIRES_KEY, 'application', applicationId];
}

export function closureKey(jobId: string): readonly unknown[] {
  return [...CLOSURE_KEY, jobId];
}

export function useHire(offerId: string): UseQueryResult<HireView, ApiError> {
  return useQuery({
    queryKey: hireKey(offerId),
    queryFn: () => apiGet<HireView>(`/offers/${offerId}/joining`),
    placeholderData: (previous) => previous,
    retry: (count, error) => error.status >= 500 && count < 2,
  });
}

/** CAND-03's section — null when the application has no accepted offer. */
export function useApplicationDocuments(applicationId: string | null): UseQueryResult<JoiningDocumentsView | null, ApiError> {
  return useQuery({
    queryKey: applicationDocumentsKey(applicationId ?? ''),
    queryFn: () => apiGet<JoiningDocumentsView | null>(`/applications/${applicationId ?? ''}/joining-documents`),
    enabled: applicationId !== null,
    placeholderData: (previous) => previous,
  });
}

export function useClosure(jobId: string): UseQueryResult<ClosureView, ApiError> {
  return useQuery({
    queryKey: closureKey(jobId),
    queryFn: () => apiGet<ClosureView>(`/jobs/${jobId}/closure`),
    placeholderData: (previous) => previous,
    retry: (count, error) => error.status >= 500 && count < 2,
  });
}

export function useEmployeeOptions(search: string, isEnabled: boolean): UseQueryResult<readonly EmployeeOption[], ApiError> {
  return useQuery({
    queryKey: ['employees', search],
    queryFn: () => apiGet<readonly EmployeeOption[]>('/employees', { query: { search } }),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

export const hiresApi = {
  confirm: (offerId: string, input: ConfirmJoiningRequest) => apiPost<HireView>(`/offers/${offerId}/joining/confirm`, input),
  uploadLink: (offerId: string) => apiPost<{ readonly text: string }>(`/offers/${offerId}/joining/upload-link`, {}),
  upload: (offerId: string, key: string, file: File, validUntil: string | null) => {
    const form = new FormData();
    form.append('file', file);
    const query = validUntil ? `?validUntil=${encodeURIComponent(validUntil)}` : '';
    return apiPost<JoiningDocumentsView>(`/offers/${offerId}/joining-documents/${encodeURIComponent(key)}${query}`, form);
  },
  review: (documentId: string, input: ReviewDocumentRequest) => apiPost<JoiningDocumentsView>(`/joining-documents/${documentId}/review`, input),
  closeOut: (jobId: string, input: CloseOutJobRequest) => apiPost<CloseOutResult>(`/jobs/${jobId}/closure`, input),
};

/** Files open in a new tab: the session cookie carries the access (same origin). */
export function documentFileUrl(documentId: string): string {
  return `/api/v1/joining-documents/${documentId}/file`;
}

// --- The candidate's documents on their offer page (D16: the link travels in a header) ----------

export function candidateDocumentsKey(token: string): readonly unknown[] {
  return ['offer-response', token, 'documents'];
}

export function useCandidateDocuments(token: string, isEnabled: boolean): UseQueryResult<CandidateDocumentsView, ApiError> {
  return useQuery({
    queryKey: candidateDocumentsKey(token),
    queryFn: async () => (await request<CandidateDocumentsView>('/offer-response/documents', { headers: { [OFFER_LINK_HEADER]: token } })).data,
    enabled: isEnabled && token !== '',
    placeholderData: (previous) => previous,
  });
}

export async function uploadCandidateDocument(token: string, key: string, file: File, validUntil: string | null): Promise<CandidateDocumentsView> {
  const form = new FormData();
  form.append('file', file);
  const query = validUntil ? `?validUntil=${encodeURIComponent(validUntil)}` : '';
  return (await request<CandidateDocumentsView>(`/offer-response/documents/${encodeURIComponent(key)}${query}`, { method: 'POST', body: form, headers: { [OFFER_LINK_HEADER]: token } })).data;
}
